import { defineMessages } from "@formatjs/intl";
import { getNoPropertiesHtml } from "@src/containers/elements/NoProperties";
import { IContext } from "@src/context";
import { pushViewItemListEventFromHtml } from "./analytics";
import { ifSearchCards } from "@src/utils/componentChecker";
import { jsonFetch } from "@src/utils/fetch";
import { getPageCategory } from "@src/utils/helper";
import { vhmserverapi } from "@src/proto/serverapi";

interface IFetchCardsHelperOption {
  offset?: number;
  limit?: number;
}

const messages = defineMessages({
  loadMore: {
    id: "SearchCards.loadMore",
    defaultMessage: "Load More",
  },
});

enum EBreadcrumb {
  Region = "Region",
  Prefecture = "Prefecture",
  City = "City",
  Trainline = "Trainline",
  Trainstation = "Trainstation",
}

enum EBreadcrumb {
  ja,
  en,
  zh,
  vi,
  pt,
  my,
  id,
  ne,
}

const PAGE_SIZE = 10;

class FetchCardsHelper {
  private offset: number;
  private limit: number;
  private hasMore: boolean;
  private pending: boolean;
  private context: IContext | null;
  private cacheListType: PlainObject | null;

  private content: string;
  private total: number;
  private loadingEle: string;

  constructor(options: IFetchCardsHelperOption) {
    this.offset = options.offset ?? 0;
    this.limit = options.limit ?? PAGE_SIZE;
    this.hasMore = true;
    this.pending = false;
    this.content = "";
    this.total = 0;
    this.context = null;
    this.cacheListType = this.getListType();
    this.loadingEle = "";
  }

  insertContext(context: IContext) {
    this.context = context;
    //check if need
    if (ifSearchCards()) {
      this.initElement();
    }
  }

  private initElement() {
    this.loadingEle = `<li class="container-search-cards-load" id="container-search-cards-load-wrap" data-pending="false">
    <button id="search-cards-load-more"> ${this.context?.intl.formatMessage(
      messages.loadMore
    )}</button>
    <div class="element-loading"></div>
    </li>`;
    this.content = this.getContainer()?.innerHTML ?? "";
  }

  insertLoad() {
    const container = this.getContainer();
    const countELe = document.querySelector("#container-search-cards-count");
    if (!container || !countELe) return;
    const count = Number(countELe.textContent);
    if (isNaN(count) || count < PAGE_SIZE) return;
    const loadWrap = document.createElement("li");
    loadWrap.classList.add("container-search-cards-load");
    loadWrap.dataset.pending = "false";
    const loadButton = document.createElement("button");
    loadButton.id = "search-cards-load-more";
    loadButton.textContent =
      this.context?.intl.formatMessage(messages.loadMore) ?? "";
    const spin = document.createElement("div");
    spin.classList.add("element-loading");

    loadWrap.append(loadButton);
    loadWrap.append(spin);
    container.append(loadWrap);
  }

  clearRequest() {
    this.offset = 0;
    this.hasMore = true;
    this.pending = false;
    this.content = "";
  }

  getListType() {
    if (this.cacheListType) return this.cacheListType;
    const searchCardsELe = document.querySelector<HTMLElement>(
      ".container-search-cards"
    );
    const defaultParam = {
      all: {},
    };
    if (!searchCardsELe || !searchCardsELe.dataset.listType) {
      return defaultParam;
    }
    const listTypeJsonString = window.atob(searchCardsELe.dataset.listType);
    try {
      const param = JSON.parse(listTypeJsonString);
      return param ?? defaultParam;
    } catch (error) {
      this.context?.errorNotifier.notify("error: get list type failed");
      return defaultParam;
    }
  }

  getBreadcrumbIds() {
    const breadcrumbEle = document.querySelector(".container-breadcrumb-list");
    if (!breadcrumbEle) {
      return [];
    }
    let child = breadcrumbEle.lastElementChild;
    const { id: endId, type: endType } = (child as HTMLElement).dataset;
    const ids = [endId];
    while (child?.previousElementSibling) {
      child = child.previousElementSibling;
      const { type, id } = (child as HTMLElement).dataset;
      if (type !== endType) break;
      ids.push(id);
    }
    return ids;
  }

  loadMore(param: PlainObject) {
    if (!this.hasMore || this.pending) return;
    this.pending = true;
    this.renderPending();
    return jsonFetch<vhmserverapi.IGetListContentOutput>(
      "/vhmserverapi.PropertyListService/GetListContent",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...param,
          page_category: getPageCategory() === "parking" ? 2 : 1,
          offset: this.offset,
          limit: this.limit,
          list_type: this.cacheListType,
          lang: EBreadcrumb[this.context?.locale ?? "ja"],
          query: location.search.slice(1),
        }),
      }
    )
      .then((res) => {
        if (!res) return;
        // @ts-expect-error tem
        this.hasMore = res.has_more ?? false;
        // @ts-expect-error tem
        this.total = res.total_count ?? 0;
        this.offset = this.offset + this.limit;
        if (!res.content) {
          this.content = this.content ? this.content : "";
        } else {
          this.content += res.content;
          // GTM push `view_item_list` event when load more and change sort
          pushViewItemListEventFromHtml(res.content);
        }
        this.renderList();
      })
      .finally(() => {
        this.pending = false;
        this.renderPending();
      });
  }

  getContainer() {
    return document.querySelector(".container-search-cards-content");
  }

  renderList() {
    const container = this.getContainer();
    if (!container) return;
    this.renderTotal();
    if (!this.content) {
      container.innerHTML = getNoPropertiesHtml(
        this.context?.intl,
        "container-search-cards-content-empty"
      );
      return;
    }
    container.innerHTML = this.hasMore
      ? this.content + this.loadingEle
      : this.content;
  }

  renderTotal() {
    const countELe = document.querySelector("#container-search-cards-count");
    if (!countELe) return;
    countELe.textContent = String(this.total);
  }

  renderPending() {
    // pending status element
    const loadEle = document.querySelector<HTMLDivElement>(
      "#container-search-cards-load-wrap"
    );
    if (!loadEle) return;
    loadEle.dataset.pending = String(this.pending);
  }
}

const fetchCardsHelper = new FetchCardsHelper({
  limit: PAGE_SIZE,
  offset: PAGE_SIZE, // PLP will render top ten in go template at first
});

export default fetchCardsHelper;
